import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      toast.error(language === 'fr' ? 'Format d\'email invalide' : 'Invalid email format');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (error) {
      // Backend always returns generic 200 to prevent enumeration; but handle network errors
      const message = error?.response?.data?.detail || error?.message || (language === 'fr' ? 'Erreur réseau' : 'Network error');
      toast.error(typeof message === 'string' ? message : (language === 'fr' ? 'Erreur' : 'Error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-secondary/30">
      <Card className="w-full max-w-md" data-testid="forgot-password-card">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-2xl">R</span>
          </div>
          <CardTitle className="font-heading text-2xl">
            {language === 'fr' ? 'Mot de passe oublié ?' : 'Forgot password?'}
          </CardTitle>
          <CardDescription>
            {sent
              ? (language === 'fr' ? 'Vérifiez votre boîte mail' : 'Check your inbox')
              : (language === 'fr' ? 'Saisissez votre email pour recevoir un lien de réinitialisation' : 'Enter your email to receive a reset link')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {sent ? (
            <div className="space-y-4 text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <p className="text-sm text-muted-foreground" data-testid="forgot-success-message">
                {language === 'fr'
                  ? `Si un compte existe pour cette adresse, vous recevrez un email avec un lien pour réinitialiser votre mot de passe. Le lien est valable 1 heure.`
                  : `If an account exists for this email, you will receive a link to reset your password. The link is valid for 1 hour.`}
              </p>
              <Button variant="outline" onClick={() => navigate('/auth')} className="w-full gap-2">
                <ArrowLeft className="h-4 w-4" />
                {language === 'fr' ? 'Retour à la connexion' : 'Back to login'}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="vous@exemple.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <Button type="submit" className="w-full h-12" disabled={loading} data-testid="forgot-submit-btn">
                {loading && <Loader2 className="h-5 w-5 mr-2 animate-spin" />}
                {language === 'fr' ? 'Envoyer le lien' : 'Send link'}
              </Button>

              <Link to="/auth" className="block text-center text-sm text-primary hover:underline">
                {language === 'fr' ? '← Retour à la connexion' : '← Back to login'}
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
